/**
 * 详情页（裁剪结果等）带入超分提质 / 智能抠图：写入图片空间并返回可创建任务的 ID。
 * 兼容本地 /uploads/ 与 OSS 受管路径（一律规范为 canonical 后再入库/展示）。
 */
import { getFundingToken, imageSpaceAPI } from '@/utils/api';
import {
  ensureImageSpaceItemsServerUrl,
  isPersistedImageUrl,
  fetchImageUrlAsDataUrl,
  normalizePersistedImageUrl,
} from '@/utils/imageUrlUtils';
import { compressImageForDatabase, ensureImageSizeWithinLimit } from '@/utils/imageUpload';
import { estimateDataUrlByteSize } from './imageSpaceStorageStats';

/**
 * 将上游 bridge 源规范为可入库/可展示的条目（不依赖图片空间写入是否成功）。
 * @param {Array<{ id?: string, name?: string, url?: string, previewUrl?: string, viewUrl?: string, displayUrl?: string }>} sources
 * @returns {Array<{ id: string, name: string, url: string, previewUrl?: string, viewUrl?: string, displayUrl?: string }>}
 */
export function normalizeIpeBridgeSources(sources) {
  const seen = new Set();
  const out = [];
  for (const s of sources || []) {
    const rawUrl = String(s?.url || s?.apiUrl || '').trim();
    const preview = String(s?.previewUrl || s?.viewUrl || s?.displayUrl || '').trim();
    const canonical = normalizePersistedImageUrl(rawUrl)
      || normalizePersistedImageUrl(preview)
      || rawUrl
      || preview;
    if (!canonical) continue;
    const id = String(s?.id || `bridge-${out.length + 1}`).trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      name: String(s?.name || id).slice(0, 120),
      url: canonical,
      previewUrl: preview || undefined,
      viewUrl: preview || undefined,
      displayUrl: preview || undefined,
    });
  }
  return out;
}

/**
 * @param {Array<{ id: string, name?: string, url: string, previewUrl?: string, viewUrl?: string, displayUrl?: string }>} sources
 * @param {{ sourceTag?: string }} options
 * @returns {Promise<{ ids: string[], urlMap: Record<string,string>, rows: object[], localSources: object[] }>}
 */
export async function importIpeSourcesToImageSpace(sources, { sourceTag = 'cropDetail' } = {}) {
  const valid = normalizeIpeBridgeSources(sources);
  if (valid.length === 0) {
    return { ids: [], urlMap: {}, rows: [], localSources: [] };
  }

  const imagesToSave = [];
  for (const s of valid) {
    let url = s.url;
    let dataUrl = '';
    if (url.startsWith('data:image/')) {
      dataUrl = url;
      url = '';
    } else if (isPersistedImageUrl(url)) {
      dataUrl = '';
    } else {
      try {
        dataUrl = await fetchImageUrlAsDataUrl(url, {
          itemOrOptions: {
            id: s.id,
            url: s.url,
            viewUrl: s.previewUrl,
            displayUrl: s.previewUrl,
            primaryImageUrl: s.url,
            primaryImageViewUrl: s.previewUrl,
          },
          getAuthToken: getFundingToken,
        });
      } catch (e) {
        console.warn('[详情预填] 拉取图片失败', s.id, e);
        // 仍保留 canonical/url，交给页面 SignedImage 现签展示
        imagesToSave.push({
          id: s.id,
          name: s.name,
          url: s.url,
          dataUrl: null,
          source: sourceTag,
          width: null,
          height: null,
          size: null,
          previewUrl: s.previewUrl,
        });
        continue;
      }
      if (!dataUrl) {
        console.warn('[详情预填] 拉取图片失败', s.id);
        imagesToSave.push({
          id: s.id,
          name: s.name,
          url: s.url,
          dataUrl: null,
          source: sourceTag,
          width: null,
          height: null,
          size: null,
          previewUrl: s.previewUrl,
        });
        continue;
      }
      url = '';
    }
    let compressed = dataUrl;
    if (dataUrl) {
      try {
        compressed = await compressImageForDatabase(dataUrl, 1000, 1000);
        compressed = await ensureImageSizeWithinLimit(compressed, 1000, 1000);
      } catch (e) {
        console.warn('[详情预填] 压缩失败', s.id, e);
        compressed = dataUrl;
      }
    }
    const sizeFromData = compressed ? estimateDataUrlByteSize(compressed) : null;
    imagesToSave.push({
      id: s.id,
      name: s.name,
      url: url || (compressed && String(compressed).trim()) || '',
      dataUrl: compressed || null,
      source: sourceTag,
      width: null,
      height: null,
      size: sizeFromData,
      previewUrl: s.previewUrl,
    });
  }

  if (imagesToSave.length === 0) {
    return { ids: [], urlMap: {}, rows: [], localSources: [] };
  }

  let converted = imagesToSave;
  try {
    converted = await ensureImageSpaceItemsServerUrl(imagesToSave);
  } catch (e) {
    console.warn('[详情预填] 转存服务端地址失败，使用原地址继续带入', e);
  }

  const toSave = converted.filter((i) => i.url && isPersistedImageUrl(i.url));
  if (toSave.length > 0) {
    try {
      await imageSpaceAPI.batchSave(toSave);
    } catch (e) {
      // 图片空间写入失败不应阻断功能页预填展示
      console.warn('[详情预填] 写入图片空间失败，仍带入本地预览', e);
    }
  }

  const source = toSave.length > 0 ? toSave : converted;
  const previewById = new Map(valid.map((s) => [s.id, s.previewUrl]));
  const ids = [];
  const urlMap = {};
  const rows = [];
  const localSources = [];

  source.forEach((img) => {
    const id = String(img.id);
    const u = String(img.url || img.dataUrl || '').trim();
    if (!u) return;
    ids.push(id);
    urlMap[id] = u;
    const preview = previewById.get(id) || img.previewUrl || undefined;
    rows.push({
      id,
      name: img.name,
      dataUrl: img.dataUrl,
      url: img.url || '',
      source: img.source || sourceTag,
      previewUrl: preview,
      viewUrl: preview,
      displayUrl: preview,
    });
    localSources.push({
      id,
      name: img.name || id,
      url: u.startsWith('data:') ? (normalizePersistedImageUrl(img.url) || u) : u,
      dataUrl: img.dataUrl && String(img.dataUrl).startsWith('data:') ? img.dataUrl : undefined,
      previewUrl: preview,
      viewUrl: preview,
      displayUrl: preview,
    });
  });

  // 若转存链路丢图，回退到规范化后的 bridge 源，保证页面至少能展示
  if (localSources.length === 0) {
    return {
      ids: valid.map((s) => s.id),
      urlMap: Object.fromEntries(valid.map((s) => [s.id, s.url])),
      rows: valid.map((s) => ({
        id: s.id,
        name: s.name,
        url: s.url,
        source: sourceTag,
        previewUrl: s.previewUrl,
        viewUrl: s.previewUrl,
        displayUrl: s.previewUrl,
      })),
      localSources: valid,
    };
  }

  return { ids, urlMap, rows, localSources };
}
